/**
 * Shared helper: load a quiz's `pages` for SCORING (not display).
 *
 * Why: the dashboard's `Recalculate` flow needs the latest scoring rules
 * (option → tag weights). If we read the quiz from Supabase, scoring uses
 * whatever was pushed there last - which may lag behind the local file
 * developers just edited. So we read LOCAL FIRST, then fall back to
 * Supabase. Recalculate then always reflects the current repo state.
 *
 * Filename convention (matches QuizController):
 *   /quizzes/<quizName>.<lang>.json
 *
 * Quiz "name" stored in Supabase doesn't always match the local stem
 * (e.g. `doyourstep-quiz` ↔ `doyourstep_v1`). Callers can pass `aliases`
 * to map name → local stem.
 */
import { QuizData, QuizPage } from '../types/quiz';

interface LoadQuizPagesArgs {
    quizName: string;
    lang: string;
    /** Optional: map Supabase quest_name → local file stem. */
    aliases?: Record<string, string>;
}

const tryFetchJson = async <T>(url: string): Promise<T | null> => {
    try {
        // Scoring rules are actively revised. Recalculate must never reuse an
        // old browser-cached quiz JSON after weights or question mappings change.
        const res = await fetch(url, { cache: 'no-store' });

        return res.ok ? ((await res.json()) as T) : null;
    } catch {
        return null;
    }
};

async function fetchLocal(quizName: string, lang: string, aliases?: Record<string, string>): Promise<QuizData | null> {
    const stem = aliases?.[quizName] || quizName;
    const langData = await tryFetchJson<QuizData>(`/quizzes/${stem}.${lang}.json`);

    if (langData) return langData;
    if (lang === 'en') return null;

    return tryFetchJson<QuizData>(`/quizzes/${stem}.en.json`);
}

async function fetchSupabase(quizName: string, lang: string): Promise<QuizData | null> {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const apiKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

    if (!supabaseUrl || !apiKey) return null;

    const headers = { 'Content-Type': 'application/json', apiKey };
    const queries = [
        `name=eq.${quizName}&lang=eq.${lang}`,
        ...(lang === 'en' ? [] : [`name=eq.${quizName}&lang=eq.en`]),
        `name=eq.${quizName}`,
    ];

    const tryQuery = async (i: number): Promise<QuizData | null> => {
        if (i >= queries.length) return null;
        const res = await fetch(`${supabaseUrl}/rest/v1/funnels?${queries[i]}`, {
            headers,
            cache: 'no-store',
        });

        if (!res.ok) return tryQuery(i + 1);
        const rows = await res.json();

        if (rows?.length > 0 && rows[0].data) return rows[0].data as QuizData;

        return tryQuery(i + 1);
    };

    return tryQuery(0);
}

/**
 * Local-first fetch - for SCORING contexts where the developer's current
 * repo state should win. Used by DashboardPage on every load and on every
 * `Recalculate` press.
 *
 * Returns just `pages` so callers don't carry the whole QuizData.
 */
export async function loadQuizPages({ quizName, lang, aliases }: LoadQuizPagesArgs): Promise<QuizPage[] | null> {
    const local = await fetchLocal(quizName, lang, aliases);

    if (local?.pages) return local.pages;

    const remote = await fetchSupabase(quizName, lang);

    return remote?.pages ?? null;
}
